import { ChangeEvent, FC, KeyboardEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Box } from '@mui/material';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useGetBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan, getBusinessToolTitle, getSetBusinessToolTitle } from 'store/useBusinessToolStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { TextInput } from 'components/_basic/Form/TextInput';
import { TooltipComponent } from 'components/_basic/TooltipComponent';
import { TOOLTIP_VARIANT } from 'components/_basic/TooltipComponent/constants';

import { getValidatedMetadata } from 'helpers/metadataParser';

import styles from './BusinessToolTitleInput.module.scss';

const MAX_TITLE_LENGTH = 150;

const titleErrorMap = new Map<string, string>([['JSON_VALIDATION_EXCEPTION', 'This name is already taken']]);

interface IProps {
  readOnly?: boolean;
}

export const BusinessToolTitleInput: FC<IProps> = ({ readOnly = false }) => {
  const { id: businessPlanId = '' } = useParams();
  const { t } = useTranslation();

  const fetchedBusinessPlan = useGetBusinessPlan({ id: businessPlanId ?? '', enabled: !!businessPlanId });

  const businessToolTitle = useBusinessToolStore(getBusinessToolTitle);
  const setBusinessToolTitle = useBusinessToolStore(getSetBusinessToolTitle);

  const { isAuthenticated, isAuthConfiguring } = useAuthenticated();

  const inputRef = useRef<HTMLInputElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);
  const prevValidTitle = useRef<string>(fetchedBusinessPlan.data?.title ?? businessToolTitle);

  const updateMutation = useUpdateBusinessPlan();

  const businessPlan = useBusinessToolStore(getBusinessPlan);

  useEffect(() => {
    if (spanRef.current && inputRef.current) {
      const spanWidth = spanRef.current.offsetWidth;
      inputRef.current.style.width = `${spanWidth + 7}px`; // Add a little extra space
    }
  }, [businessToolTitle]);

  const handleTitleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (value.length <= MAX_TITLE_LENGTH) {
      setBusinessToolTitle(value);
      updateMutation.reset();
    }
  };

  const handleBlur = async () => {
    if (!businessToolTitle) {
      setBusinessToolTitle(prevValidTitle.current || '');
      return;
    }

    const trimmedTitle = businessToolTitle.trim();

    if (!(isAuthenticated || isAuthConfiguring)) {
      prevValidTitle.current = businessToolTitle;
      return;
    }

    if (businessToolTitle !== prevValidTitle.current && !updateMutation.error) {
      try {
        const params = {
          id: businessPlanId,
          body: { metadata: JSON.stringify(getValidatedMetadata(businessPlan.metadata)), title: trimmedTitle },
        };
        await updateMutation.mutateAsync(params);
        prevValidTitle.current = trimmedTitle;
      } catch (error) {
        return;
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  const errorMessage = titleErrorMap.get(updateMutation.error?.message) ?? updateMutation.error?.message ?? '';
  const title = !isAuthenticated && !readOnly ? 'Document 1' : businessToolTitle;

  return (
    <TooltipComponent title={t('rename')} placement="bottom" variant={TOOLTIP_VARIANT.SMALL} isTooltipHidden={readOnly}>
      <Box className={styles.inputWrapper}>
        <TextInput
          value={title}
          variant="standard"
          className={styles.textInput}
          onChange={handleTitleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          inputRef={inputRef}
          inputProps={{ maxLength: MAX_TITLE_LENGTH, style: { width: 'auto', minWidth: '80px', maxWidth: '235px' } }}
          fieldError={readOnly ? undefined : { message: errorMessage }}
          fieldClassName={styles.field}
          readOnly={readOnly}
        />
        <span ref={spanRef} className={styles.fakeInputValue}>
          {title}
        </span>
      </Box>
    </TooltipComponent>
  );
};
